import { useEffect } from 'react';
import { applyChatSearchHighlight } from './ChatSearch.highlight';
import { useChatSearchContext } from './ChatSearch.context';

/**
 * Keeps the in-chat search marks on the bubbles of the loaded matches: every
 * match in yellow, and the one the search is on (`activeIndex`) underlined.
 *
 * The active mark follows the search, not the gestures: like a browser's find
 * bar, it stays on the current match until the user steps or closes, so
 * scrolling away and back still shows where the counter is.
 *
 * Called by MessageContainer, which owns the live node registry. Re-drawn when
 * the matches or the step change and whenever the thread does (`thread`, the
 * rendered events): a `?around=` window or a new page mounts new bubbles, and a
 * range on an unmounted text node marks nothing. A no-op outside a
 * ChatSearchProvider.
 *
 * @param params.resolveNode - Event id to its live bubble node.
 * @param params.thread - The events on screen; only its identity is read.
 */
export default function useChatSearchHighlight(params: {
  resolveNode: (eventId: string) => HTMLElement | null;
  thread: unknown;
}): void {
  const { resolveNode, thread } = params;
  const chatSearch = useChatSearchContext();
  const isOpen = chatSearch?.isOpen ?? false;
  const matches = chatSearch?.matches;
  const terms = chatSearch?.searchTerms;
  const activeEventId = matches?.[chatSearch?.activeIndex ?? -1]?.eventId;

  useEffect(() => {
    if (!isOpen || !matches?.length || !terms?.length) return;

    const nodes = matches
      .map((match) => resolveNode(match.eventId))
      .filter((node): node is HTMLElement => Boolean(node));
    const activeNode = activeEventId ? resolveNode(activeEventId) : null;

    return applyChatSearchHighlight({ nodes, activeNode, terms });
  }, [isOpen, matches, terms, activeEventId, thread, resolveNode]);
}
